import { createContext, useState, useContext, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail, 
  sendEmailVerification,
  onAuthStateChanged,
  type User as FirebaseUser
} from 'firebase/auth';
import { auth } from './firebase';
import api from './api';
import { authService } from './authService';
import { ENDPOINTS } from '../config';
import type { User } from '../types';

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  firebaseUser: FirebaseUser | null;
  login: (email: string, password: string) => Promise<User | null>;
  signup: (email: string, password: string, additionalData?: any) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  fetchUser: () => Promise<User | null>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<User | null> => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      setIsLoggedIn(false);
      setIsLoading(false);
      return null;
    }
    try {
      const response = await api.get<User>(ENDPOINTS.GET_ME);
      setUser(response.data);
      setIsLoggedIn(true);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user. Session may be invalid.", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sync Firebase Auth State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fUser) => {
      setFirebaseUser(fUser);
      if (fUser) {
        const token = await fUser.getIdToken();
        authService.setToken(token);
        setIsLoggedIn(true);
        await fetchUser();
      } else {
        authService.clearToken();
        setUser(null);
        setIsLoggedIn(false);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [fetchUser]);

  const login = async (email: string, password: string): Promise<User | null> => {
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      authService.setToken(token);
      setIsLoggedIn(true);
      return await fetchUser();
    } catch (error: any) {
      setIsLoading(false);
      throw new Error(error.message || 'Login failed');
    }
  };

  const signup = async (email: string, password: string, additionalData?: any): Promise<void> => {
    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Trigger Email Verification immediately
      await sendEmailVerification(userCredential.user);
      
      // Optional: Sync additional data to backend
      if (additionalData) {
          await api.post(ENDPOINTS.REGISTER, {
              email,
              firebase_uid: userCredential.user.uid,
              ...additionalData
          });
      }
      
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      throw new Error(error.message || 'Signup failed');
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
      try {
          await sendPasswordResetEmail(auth, email);
      } catch (error: any) {
          throw new Error(error.message || 'Password reset request failed');
      }
  };

  const logout = async () => {
    await signOut(auth);
    authService.clearToken();
    setUser(null);
    setIsLoggedIn(false);
    if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/profile')) {
      window.location.href = '/';
    }
  };

  const authValue = useMemo(() => ({
    isLoggedIn,
    user,
    firebaseUser,
    login,
    signup,
    logout,
    forgotPassword,
    fetchUser,
    isLoading
  }), [isLoggedIn, user, firebaseUser, login, signup, logout, forgotPassword, fetchUser, isLoading]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
